import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../custom-type'
import { organization, user } from '../auth'
import { Challenge, LessonModule } from '../content'
import { Course } from './course'
import { LearnProgress, QuizAttempt } from './tracker'

export const LearnEnrollment = pgTable(
  'learn_enrollment',
  {
    id: uuid().primaryKey().defaultRandom(),
    courseId: uuid().references(() => Course.id, { onDelete: 'cascade' }),
    challengeId: uuid().references(() => Challenge.id, { onDelete: 'cascade' }),
    lessonModuleId: uuid().references(() => LessonModule.id, { onDelete: 'cascade' }),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    status: text({ enum: ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'FAILED'] })
      .notNull()
      .default('NOT_STARTED'),
    progress: numericAsNumber().notNull().default(0),
    startedAt: timestamp({ mode: 'date', withTimezone: true }),
    completedAt: timestamp({ mode: 'date', withTimezone: true }),
    timeTotal: integer(), // in seconds
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),

    // DEAD FLAGS — all three pairs below fed the deleted per-table producers (and the leaderboard is
    // now computed live). Nothing reads them; the daily lookup tables are recomputed from these rows by
    // `compute<Metric>` (packages/trigger/src/metrics/). Kept until a migration drops them.
    // `updatedAt` above still matters: lesson_history_daily anchors on it and
    // lesson_enrollment_metrics' stale guard compares it with `calculatedAt`.

    // Lesson metrics daily tracking (lookup table aggregation)
    calculatedForLessonMetricsDaily: boolean().default(false).notNull(),
    lessonMetricsDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Lesson history daily tracking (lookup table aggregation)
    calculatedForLessonHistoryDaily: boolean().default(false).notNull(),
    lessonHistoryDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Leaderboard entry tracking (lookup table aggregation)
    calculatedForLeaderboard: boolean().default(false).notNull(),
    leaderboardCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),
  },
  (table) => [
    index('idx_learn_enrollment_course').on(table.courseId),
    index('idx_learn_enrollment_lesson_metrics_daily').on(table.calculatedForLessonMetricsDaily),
    index('idx_learn_enrollment_lesson_history_daily').on(table.calculatedForLessonHistoryDaily),
    index('idx_learn_enrollment_leaderboard').on(table.calculatedForLeaderboard),
    index('idx_learn_enrollment_challenge').on(table.challengeId),
    index('idx_learn_enrollment_lesson_module').on(table.lessonModuleId),
    index('idx_learn_enrollment_user').on(table.userId),
    // The today overlay of lesson_enrollment_metrics and the nightly activity scan: enrollments of a user touched since X.
    index('idx_learn_enrollment_user_updated').on(table.userId, table.updatedAt),
  ],
)

export const learnEnrollmentRelations = relations(LearnEnrollment, ({ one, many }) => ({
  course: one(Course, {
    fields: [LearnEnrollment.courseId],
    references: [Course.id],
  }),

  challenge: one(Challenge, {
    fields: [LearnEnrollment.challengeId],
    references: [Challenge.id],
  }),

  lessonModule: one(LessonModule, {
    fields: [LearnEnrollment.lessonModuleId],
    references: [LessonModule.id],
  }),

  user: one(user, {
    fields: [LearnEnrollment.userId],
    references: [user.id],
  }),

  organization: one(organization, {
    fields: [LearnEnrollment.organizationId],
    references: [organization.id],
  }),

  learnProgresses: many(LearnProgress),
  quizAttempts: many(QuizAttempt),
}))

export const InsertLearnEnrollmentSchema = createInsertSchema(LearnEnrollment)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    progress: true,
  })
  .superRefine((data, ctx) => {
    // Validate that exactly one of courseId, challengeId, or lessonModuleId is provided
    const providedCount = [data.courseId, data.challengeId, data.lessonModuleId].filter(Boolean).length

    if (providedCount === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Exactly one of courseId, challengeId, or lessonModuleId must be provided',
        path: ['courseId'],
      })
    }

    if (providedCount > 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Only one of courseId, challengeId, or lessonModuleId can be provided',
        path: ['courseId'],
      })
    }

    if (data.startedAt && data.completedAt && data.startedAt > data.completedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'Started at must be before completed at',
        path: ['startedAt'],
      })
    }
  })

export const UpdateLearnEnrollmentSchema = createUpdateSchema(LearnEnrollment)
  .partial()
  .omit({
    id: true,
    userId: true,
    organizationId: true,
    courseId: true,
    challengeId: true,
    lessonModuleId: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.startedAt && data.completedAt && data.startedAt > data.completedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'Started at must be before completed at',
        path: ['startedAt'],
      })
    }
  })

export const SelectLearnEnrollmentSchema = createSelectSchema(LearnEnrollment)

export type InsertLearnEnrollment = typeof LearnEnrollment.$inferInsert
export type UpdateLearnEnrollment = z.infer<typeof UpdateLearnEnrollmentSchema>
export type SelectLearnEnrollment = typeof LearnEnrollment.$inferSelect
